import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Chip,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import { MoreVert as MoreVertIcon, Add, Inventory, LocalShipping, Delete, History } from '@mui/icons-material';

// --- PLACEHOLDER API AND AUTH CONTEXTS (Assume these are imported from project) ---
// NOTE: Replace this with your actual imports.
import { useAuth } from '../../../hooks/useAuth';
import { getInventoryLogsAPI, getlistMenu, getMenuInventory, linkMenuItemAPI, recordLogAPI } from '../../../hooks/apiHandler';
import { LinkMenuModal } from './invtpopupModels';
import { InventoryLogModal } from './component/inventoryLogModel';



const getChipColor = (current, threshold) => current < threshold ? 'error' : 'success';

// --- Sub-Components ---
const QuickStats = ({ summary }) => (
  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', my: 2 }}>
    <Chip
      icon={<Inventory />}
      label={`Total Items: ${summary.numberOfItems}`}
      color="primary"
      variant="filled"
    />
    <Chip
      icon={<LocalShipping />}
      label={`Stock Value: $${summary.totalStockValue.toFixed(2)}`}
      color="secondary"
      variant="outlined"
    />
    <Chip
      icon={<Delete />}
      label={`Low Stock Alerts: ${summary.lowStockCount}`}
      color={summary.lowStockCount > 0 ? 'error' : 'success'}
      variant="filled"
    />
  </Box>
);

const LogTable = ({ itemList, logs }) => (
  <TableContainer elevation={0} sx={{ maxHeight: 350, border: '1px solid #eee' }}>
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
          <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty Change</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Cost</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {logs.length === 0 ? (
          <TableRow><TableCell colSpan={5} align="center">No logs found for this type.</TableCell></TableRow>
        ) : (
          logs.map((log) => (

            <TableRow key={log._id} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
              <TableCell>{new Date(log.dateTime).toLocaleDateString()}</TableCell>
              <TableCell>{(itemList) ? itemList.find(i => i._id === log.inventoryItemID)?.item || 'Unknown Item': "ListNotFound"}</TableCell>
              <TableCell align="right" sx={{ color: log.quantityChange < 0 ? 'error.main' : 'success.main', fontWeight: 'bold' }}>{log.quantityChange}</TableCell>
              <TableCell align="right">${log.unitCost.toFixed(2)}</TableCell>
              <TableCell>{log.notes}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

// --- Main Component ---
export const InventoryPage = () => {
  const { Logout } = useAuth();
  var storeID = useRef(JSON.parse(window.localStorage.getItem('ds')));

  const [anchorEl, setAnchorEl] = useState(null);
  const [mainTab, setMainTab] = useState(0);
  const [logTab, setLogTab] = useState(0);

  const [listMenuInventory, setMenuInventory] = useState([]); /// this is for Existing Menu list.
  const [invtrySummary, setInvtrySummary] = useState({
    numberOfItems: 0,
    totalStockValue: 0.00,
    lowStockCount: 0
  });
  const [listMenu, setMenu] = useState([]); /// this is for Existing Menu list.
  const [openLinkMenu, setOpenLinkMenu] = useState(false); // State for the new modal
  const [openLogDialog, setLogDialog] = useState(false); // State for the new modal

  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [allLogs, setAllLogs] = useState({ DELIVERY: [], COUNT_ADJUSTMENT: [], SPOILAGE: [] });


  const isMenuOpen = Boolean(anchorEl);

  // Map inner tab index to logType string (matching the backend enum)
  const logTypeMap = {
    0: 'DELIVERY',
    1: 'COUNT_ADJUSTMENT',
    2: 'SPOILAGE',
    3: 'RECIPE_CONSUMPTION', // We use SPOILAGE here; the backend can include WASTE if needed
    4: 'INTERNAL_USE'

  };

  useMemo(() => {
    async function calldeviceList() {
      var response = await getlistMenu({ storeID: storeID.current }, Logout);
      setMenu(await response.data);
    }
    calldeviceList()
  }, [Logout]);

  // --- API Integration: Fetch Logs ---
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        if (mainTab === 1) {
          console.log("Fetching logs for tab:", logTab);
          const currentLogType = logTypeMap[logTab];
          setIsLoadingLogs(true);
          try {
            // NOTE: Use the currentLogType to filter logs at the API level for performance
            const response = await getInventoryLogsAPI({ storeID: storeID.current, currentLogType }, Logout);
            console.log(`Logs fetched for :`, response.data);
            setAllLogs(prev => ({
              ...prev,
              [currentLogType]: response.data || []
            }));
          } catch (error) {
            console.error(`Failed to fetch logs for ${currentLogType}:`, error);
            // Optionally show error message in UI
          }
          setIsLoadingLogs(false);



        } else if (mainTab === 0) {
          setIsLoadingLogs(true);
          /// time to fetch inventory list.
          // console.log("Fetch inventory list data here...");
          const stockResponse = await getMenuInventory({ storeID: storeID.current }, Logout);
          // console.log("Inventory Data:", stockResponse.data);
          setMenuInventory(stockResponse.data || []);
          setInvtrySummary({
            numberOfItems: stockResponse.data.length,
            totalStockValue: stockResponse.data.reduce((acc, item) => acc + (item.currentStock * item.pCost), 0),
            lowStockCount: stockResponse.data.filter(item => item.currentStock < item.minThr).length
          });
          setIsLoadingLogs(false);
        }
      } catch (error) {
        console.error("Error in fetching inventory data:", error);
      }
    }
    fetchLogs();
    // eslint-disable-next-line
  }, [mainTab, logTab, storeID, Logout]); // Re-fetch when main or inner tab changes

  // Placeholder functions
  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleInnerTabChange = (event, newValue) => setLogTab(newValue);
  const handleMainTabChange = (event, newValue) => setMainTab(newValue);

  const handleAction = (action) => {
    // NOTE: In a real app, this would open a modal/dialog for data entry

    setLogDialog(true);
  };
  const handleLinkItemSelect = async (item) => {
    // trigger manual link process here...
    var payload = {
      storeID: storeID.current,
      ...item
    };
    var serverResponse = await linkMenuItemAPI(payload, Logout);
    setMenuInventory(prev => [...prev, serverResponse.data]);
    setOpenLinkMenu(false);
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Inventory Dashboard
      </Typography>

      {/* --- 1. Quick Summary Section --- */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, backgroundColor: '#e8f5e9' }}>
        <Typography variant="h6" color="primary.dark" gutterBottom>Quick Summary</Typography>
        <QuickStats summary={invtrySummary} />
      </Paper>

      {/* Action Header and Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" fontWeight="bold">{mainTab === 0 ? 'Product Stock List' : 'Inventory Transactions Log'}</Typography>

          {/* Right-aligned Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>

            {/* Only show Add/Link buttons on the Stock List tab */}
            {mainTab === 0 && (
              <>
                <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => setOpenLinkMenu(true)}>
                  Link Menu Item
                </Button>

                <Button variant="contained" color="secondary" startIcon={<Add />} onClick={() => handleAction('ADD_NEW_INVENTORY')}>
                  New Stock Item
                </Button>
              </>
            )}

            {/* Record Button with Dropdown (Always visible for quick access) */}
            <Button
              variant="contained"
              color="success"
              onClick={() => setLogDialog(true)}
              endIcon={<History />}
            >
              Record
            </Button>


            {/* More Options Button */}
            <IconButton onClick={handleMenuClick} color="default">
              <MoreVertIcon />
            </IconButton>
            <Menu id="csv-menu" anchorEl={anchorEl} open={isMenuOpen} onClose={handleMenuClose}>
              <MenuItem onClick={() => console.log('UPLOAD_CSV')}>Upload CSV</MenuItem>
              <MenuItem onClick={() => console.log('DOWNLOAD_CSV')}>Download Inventory CSV</MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Main Tab Navigation */}
        <Tabs value={mainTab} onChange={handleMainTabChange} aria-label="inventory main tabs">
          <Tab label="Product Stock List" />
          <Tab label="Transaction Logs" />
        </Tabs>
      </Box>

      {/* --- 2 & 3. Main Content Area (Conditional Rendering) --- */}
      <Paper elevation={3} sx={{ p: 3, backgroundColor: mainTab === 0 ? '#f0f4f8' : '#f3e5f5' }}>
        {/* --- 2. Inventory Product List Table (Section visible when mainTab === 0) --- */}
        {mainTab === 0 && (isLoadingLogs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Item Name / SKU</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Stock (Units)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Threshold</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cost</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Margin</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
             
                {listMenuInventory.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center">No Inventory found</TableCell></TableRow>
                ) : (
                  listMenuInventory.map((item) => (
                    <TableRow
                      key={item._id}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#ffffff' },
                        '&:hover': { backgroundColor: '#e3f2fd', cursor: 'pointer' },
                        backgroundColor: item.currentStock < item.lowThreshold ? '#ffebee' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Typography fontWeight="bold">{item.item}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.sku}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box component="span" sx={{ color: getChipColor(item.currentStock, item.minThr) === 'error' ? 'error.main' : 'success.main', fontWeight: 'bold' }}>
                          {item.currentStock} {item.unit}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{item.minThr} - {item.maxThr}</TableCell>
                      <TableCell>{item.pCost}</TableCell>
                      <TableCell>{item.price}</TableCell>
                      <TableCell>{(((item.price - item.pCost) / item.price) * 100).toFixed(2)} %</TableCell>
                    </TableRow>
                  )))}
              </TableBody>
            </Table>
          </TableContainer>)
        )}

        {/* --- 3. Inventory Log Section (Visible when mainTab === 1) --- */}
        {mainTab === 1 && (
          isLoadingLogs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Tabs value={logTab} onChange={handleInnerTabChange} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Deliveries (Incoming)" />
                <Tab label="Count Adjustments" />
                <Tab label="Spoilage / Waste" />
                <Tab label="Sold" />
                <Tab label="Internal Use" />
              </Tabs>

              <Box>
                {logTab === 0 && <LogTable itemList={listMenuInventory} logs={allLogs.DELIVERY} />}
                {logTab === 1 && <LogTable itemList={listMenuInventory} logs={allLogs.COUNT_ADJUSTMENT} />}
                {logTab === 2 && <LogTable itemList={listMenuInventory} logs={allLogs.SPOILAGE} />}
                {logTab === 3 && <LogTable itemList={listMenuInventory} logs={allLogs.RECIPE_CONSUMPTION || []} />}
                {logTab === 4 && <LogTable itemList={listMenuInventory} logs={allLogs.INTERNAL_USE || []} />}
              </Box>
            </Box>
          )
        )}
      </Paper>
      <LinkMenuModal
        open={openLinkMenu}
        onClose={() => setOpenLinkMenu(false)}
        menuData={listMenu}
        onLinkItemSave={handleLinkItemSelect}
      />
      <InventoryLogModal
        open={openLogDialog} // Control this state as needed
        onClose={() => { setLogDialog(false) }} // Implement close handler
        inventoryItems={listMenuInventory}
        onLogSave={async (data) => {

          var payload = {
            storeID: storeID.current,
            productList: data
          };
          console.log("Linking payload:", payload);
          var serverResponse = await recordLogAPI(payload, Logout);
          console.log("Link Menu Item Response:", serverResponse);
          //setMenuInventory(prev => [...prev, serverResponse.data]);
          setLogDialog(false)

        }} // Implement save handler
      />
    </Container>
  );
};
